import { gql } from '@apollo/client';
import client from 'apollo/client';

/**
 * Create group session method (creates N recurring sessions)
 * @param {Object} payload
 * @returns {Promise}
 */
const create = (payload) =>
  client.mutate({
    mutation: gql`
      mutation createGroupSession($input: CreateGroupSessionInput!) {
        createGroupSession(input: $input) {
          id
          start_date
          end_date
          recurrence_group_id
          status
        }
      }
    `,
    variables: { input: payload }
  });

/**
 * List group sessions (admin — paginated)
 * @param {Object} payload
 * @returns {Promise}
 */
const list = (payload) => {
  const { pagination = { first: 20, page: 1 }, filters = {} } = payload;

  // status: string enum → array para @in
  const statusFilter = filters.status ? `status: [${filters.status}]` : '';

  // trainer_id: array de {value, label} → extraer IDs
  const trainerIds = (filters.trainer_id || []).filter(Boolean).map((t) => t.value);
  const trainerFilter = trainerIds.length > 0 ? `trainer_id: [${trainerIds.join(',')}]` : '';

  // session_type_id: array de valores numéricos (MultipleSelect)
  const sessionTypeIds = (filters.session_type_id || []).filter(Boolean);
  const sessionTypeFilter =
    sessionTypeIds.length > 0 ? `session_type_id: [${sessionTypeIds.join(',')}]` : '';

  // start_between: { startDate, endDate } desde DateRangePickerFieldAdapter
  let startBetweenFilter = '';
  if (filters.start_between?.startDate) {
    const endPart = filters.start_between.endDate
      ? `, end_date: "${filters.start_between.endDate}"`
      : '';
    startBetweenFilter = `start_between: { start_date: "${filters.start_between.startDate}"${endPart} }`;
  }

  return client.query({
    query: gql`
      query groupSessions {
        groupSessions(
          ${statusFilter}
          ${trainerFilter}
          ${sessionTypeFilter}
          ${startBetweenFilter}
          orderBy: [{ column: START_DATE, order: DESC }]
          first: ${pagination.first}
          page: ${pagination.page}
        ) {
          paginatorInfo {
            currentPage
            hasMorePages
            total
          }
          data {
            id
            start_date
            end_date
            status
            observations
            recurrence_group_id
            zoom_meeting_id
            trainer {
              id
              full_name
              photo
              online_meeting_url
            }
            session_type_id {
              id
              name
            }
            attendances {
              id
              status
              user {
                id
                full_name
                photo
              }
            }
          }
        }
      }
    `,
    fetchPolicy: 'network-only'
  });
};

/**
 * List upcoming group sessions for Active Plus client
 * @returns {Promise}
 */
const myGroupSessions = () =>
  client.query({
    query: gql`
      query myGroupSessions {
        myGroupSessions {
          id
          start_date
          end_date
          status
          observations
          recurrence_group_id
          trainer {
            id
            full_name
            photo
            online_meeting_url
          }
          session_type_id {
            id
            name
          }
          attendances {
            id
            status
            user {
              id
            }
          }
        }
      }
    `,
    fetchPolicy: 'network-only'
  });

/**
 * Update group session (status, observations)
 * @param {Object} payload
 * @returns {Promise}
 */
const update = (payload) =>
  client.mutate({
    mutation: gql`
      mutation updateGroupSession($id: ID!, $input: UpdateGroupSessionInput!) {
        updateGroupSession(id: $id, input: $input) {
          id
          status
        }
      }
    `,
    variables: { id: payload.id, input: payload.input }
  });

/**
 * Mark attendance for a group session
 * @param {Object} payload
 * @returns {Promise}
 */
const markAttendance = (payload) =>
  client.mutate({
    mutation: gql`
      mutation markGroupSessionAttendance($group_session_id: ID!, $attendances: [AttendanceInput!]!) {
        markGroupSessionAttendance(
          group_session_id: $group_session_id
          attendances: $attendances
        ) {
          id
          status
          user {
            id
            full_name
          }
        }
      }
    `,
    variables: {
      group_session_id: payload.group_session_id,
      attendances: payload.attendances
    }
  });

/**
 * Delete a group session (soft delete)
 * @param {string|number} id
 * @returns {Promise}
 */
const deleteSession = (ids) =>
  client.mutate({
    mutation: gql`
      mutation deleteGroupSession($id: [ID!]!) {
        deleteGroupSession(id: $id) {
          id
        }
      }
    `,
    variables: { id: Array.isArray(ids) ? ids : [ids] }
  });

/**
 * Sincroniza la asistencia de una sesión grupal desde Zoom (≥ umbral de minutos = PRESENTE).
 * @param {string|number} groupSessionId
 * @returns {Promise}
 */
const syncZoomAttendance = (groupSessionId) =>
  client.mutate({
    mutation: gql`
      mutation syncGroupSessionZoomAttendance($id: ID!) {
        syncGroupSessionZoomAttendance(group_session_id: $id) {
          id
          status
          attendances {
            id
            status
            user {
              id
            }
          }
        }
      }
    `,
    variables: { id: groupSessionId }
  });

/**
 * Fetch all Active Plus clients for attendance marking
 * @returns {Promise}
 */
// Roster de asistencia: clientes Active Plus con PLAN ACTIVO (para no listar registros masivos de
// inactivos). Los que ya estén marcados en la sesión pero hoy estén inactivos NO se pierden: el
// panel (GroupAttendancePanel) une este roster con `session.attendances`.
// Nota dev: en entornos sin miembros activos el roster sale corto (esperado); en prod muestra activos.
const activePlusClients = () =>
  client.query({
    query: gql`
      query activePlusClients {
        users(role: CLIENT, can_active_plus: true, membership_status: [ACTIVE], first: 500, page: 1, orderBy: { column: FIRST_NAME, order: ASC }) {
          data {
            id
            full_name
            photo
            created_at
          }
        }
      }
    `,
    fetchPolicy: 'network-only'
  });

export default { create, list, myGroupSessions, update, markAttendance, activePlusClients, deleteSession, syncZoomAttendance };
